// Last-team memory helper (server-backed)
//
// Apr 2026 — Persists the user's most recently selected team to the
// SERVER (users.last_active_team_id) so it syncs across devices.
// Replaces an earlier localStorage-based approach which only worked
// per-browser.
//
// Why server-backed:
//   • A coach who uses both desktop and mobile gets the same starting
//     team on each device — no surprise "wait, why am I on Team A on
//     my phone but Team B on my laptop?"
//   • Cache clears, incognito tabs, and switching browsers all
//     preserve the preference.
//   • Single source of truth is the database — easier to debug and
//     safer for analytics later.
//
// Flow:
//   1. Server sends `lastActiveTeamId` in /auth/login, /auth/me,
//      and OAuth response payloads.
//   2. Layouts pick the right team via pickActiveTeam(teams,
//      currentActive, lastActiveTeamId).
//   3. When the user changes team, layouts call saveLastTeamId(teamId)
//      which fires PATCH /auth/last-team. Fire-and-forget — failures
//      don't block navigation.

import api from '../services/api.js';

// Debounce timer — when the user clicks through teams rapidly, we
// only PATCH the LATEST selection. Without this, a fast tap-tap-tap
// could race against itself and leave the DB recording the
// next-to-last selection. The debounce window is short (250ms) so
// users get the persistence behavior they expect — close the tab
// quickly and your last selection still saves.
let saveTimer = null;
let pendingTeamId = null;

/**
 * Cancel any pending debounced save. Call this on logout to avoid
 * a race where User A's pending team selection fires AFTER User B
 * has logged in on the same device — the request would go with
 * User B's token but User A's intended team id, potentially writing
 * an unrelated team to User B's last_active_team_id.
 */
export const cancelPendingSave = () => {
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  pendingTeamId = null;
};

/**
 * Persist the user's selected team id to the server.
 * Pass null to clear the preference.
 *
 * Debounced (250ms) — rapid switches only PATCH the final value.
 * Fire-and-forget — failures don't block UI.
 */
export const saveLastTeamId = (teamId) => {
  pendingTeamId = teamId || null;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    const idToSave = pendingTeamId;
    saveTimer = null;
    api.patch('/auth/last-team', { teamId: idToSave }).catch(err => {
      // Swallow — UI shouldn't be blocked by this. Common failure
      // modes: stale token (401), network blip, server restart. The
      // user's selection is still active in the React state for this
      // session; we just won't restore it on next load.
      if (err?.response?.status !== 401) {
        console.warn('Failed to save last-active team:', err?.message);
      }
    });
  }, 250);
};

/**
 * Pick the right team to make active given a list of teams from
 * the API, preferring (in order):
 *   1. The currently-active team if it still exists in the list
 *      (preserves selection across mid-session refreshes — e.g. when
 *      the team list refreshes after a +New Team action)
 *   2. The user's saved `lastActiveTeamId` from the server (if
 *      provided and the team is still in the list)
 *   3. The first team in the list (legacy fallback for users with
 *      no saved preference)
 *   4. null if the list is empty
 *
 * Returns the chosen team object, ready to pass to setActiveTeam().
 */
export const pickActiveTeam = (teams, currentActive = null, lastActiveTeamId = null) => {
  if (!Array.isArray(teams) || teams.length === 0) return null;
  // Already-active team takes precedence — keep selection stable on
  // mid-session refreshes.
  if (currentActive) {
    const stillThere = teams.find(t => t.id === currentActive.id);
    if (stillThere) return stillThere;
  }
  // 2026-07-10 — locally-remembered choice. Written at sidebar-switch
  // time BEFORE the full-page reload that switching now performs
  // (DesktopLayout), so boot restores the team the user just clicked.
  // Must WIN over the server's lastActiveTeamId: the PATCH
  // /auth/last-team fires asynchronously and can be cancelled by that
  // very reload (or rate-limited — 2026-07-04 incident), leaving the
  // server value one switch behind. Root cause of "clicking a team in
  // the sidebar didn't load that team".
  try {
    const localId = localStorage.getItem('gd:lastTeamId');
    if (localId) {
      const localMatch = teams.find(t => t.id === localId);
      if (localMatch) return localMatch;
    }
  } catch { /* storage unavailable — fall through */ }
  // Saved preference from server.
  if (lastActiveTeamId) {
    const match = teams.find(t => t.id === lastActiveTeamId);
    if (match) return match;
    // The saved team is no longer accessible (deleted, kicked, etc.)
    // — fall through to teams[0]. The next saveLastTeamId() will
    // overwrite the stale id automatically.
  }
  return teams[0];
};
